import { z } from 'zod'
import { mongoId, mongoIdSchema } from '../utils/basicSchemas/_id.z.schema'
import { optionalString, requiredString, timestampedDocSchema } from '../utils/basicSchemas/document.z.schema'
import { ZSchema } from '../utils/zodSchema'

/**
 * Zod Enum schema representing the possible types for a Notification.
 */
const notificationTypesEnum = z.enum([
    "notif-like",
    "notif-comment",
    "notif-follow",
    "notif-donation",
    "notif-transaction",
    "notif-bonus",
    "notif-wallet",
    "notif-post",
    "notif-tag",
    "notif-collaborator",
    "notif-tag-user",
    "notif-gallery-content",
])

/**
 * Zod schema representing the possible owner types for a Notification.
 */
const ownerTypeEnum = z.enum(["Post", "User", "Gallery"])

/**
 * Zod schema representing a Notification.
 * 
 * This schema validates the structure and data of a notification object.
 * 
 * Fields:
 * - `title`: A required string that represents the notification's title.
 * - `subtitle`: An optional string that represents the notification's subtitle.
 * - `amount`: An optional number representing an associated value (for example: donation amount).
 * - `caller`: A required Mongo ObjectId referencing the entity initiating the notification.
 * - `sender`: A required Mongo ObjectId referencing the sender of the notification.
 * - `type`: A required enum value indicating the notification type.
 * - `ownerType`: A required enum value indicating the type of the owner (User, Post, or Gallery).
 * - `owner`: A required Mongo ObjectId referencing the owner of the notification (could be a user, post, or gallery).
 * 
 * @example
 * notificationSchema.parse({
 *     title: "New Like",
 *     caller: "603f650a0953de235c89eaba",
 *     sender: "603f650a0953de235c89eabb",
 *     type: "notif-like",
 *     ownerType: "Post",
 *     owner: "603f650a0953de235c89eabc"
 * });
 */
const notificationSchema = z.object({
    title: requiredString("Notification Title"),
    subtitle: z.string().optional(),
    amount: z.number().optional(),
    caller: mongoIdSchema,
    sender: mongoIdSchema,
    type: notificationTypesEnum,
    ownerType: ownerTypeEnum,
    owner: mongoId("Notification Owner"),
})

const notificationCreationSchema = z.object({
    title: requiredString("Notification Title"),
    subtitle: optionalString("Notification Subtitle"),
})

const notificationIdSchema = z.object({
    id: mongoId("Notification id"),
})


/**
 * Zod schema representing a Notification document.
 * 
 * This schema extends {@link notificationSchema} by merging with {@link timestampedDocSchema} 
 * to include fields inherent to Mongoose documents like timestamps, `_id`, and `__v`.
 * 
 * @example
 * notificationSchemaDoc.parse({
 *     _id: "603f650a0953de235c89eadc",
 *     __v: "1",
 *     createdAt: new Date(),
 *     updatedAt: new Date(),
 *     title: "New Like",
 *     caller: "603f650a0953de235c89eaba",
 *     sender: "603f650a0953de235c89eabb",
 *     type: "notif-like",
 *     ownerType: "Post",
 *     owner: "603f650a0953de235c89eabc"
 * });
 */
const notificationSchemaDoc = timestampedDocSchema.merge(notificationSchema)

// Infer and Export types
type NotificationSchemaType = z.infer<typeof notificationSchema>
type NotificationSchemaDocType = z.infer<typeof notificationSchemaDoc>

// Instance and export Validation objects 
const notificationValidator = new ZSchema({bodySchema : notificationSchemaDoc})
const notificationCreationValidator = new ZSchema({bodySchema : notificationCreationSchema})
const notificationIdValidator = new ZSchema({paramSchema : notificationIdSchema})



export { NotificationSchemaType, NotificationSchemaDocType }

export { notificationSchema, notificationSchemaDoc }
export { notificationValidator, notificationCreationValidator, notificationIdValidator }
